import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  IPayerAutoSelectionRule,
  IPayerAutoSelectionRuleSaveRequest,
  PayerAutoSelectionRuleKind,
  PayerAutoSelectionSourceScope,
} from "types/PayerAutoSelection/PayerAutoSelection";

import {
  KIND_CHOICES,
  OPTION_TEXT,
  SOURCE_CHOICES,
} from "./payerAutoSelectionWording";

interface IDraft {
  kind: PayerAutoSelectionRuleKind;
  /** One keyword per line, as typed. */
  keywordsText: string;
  matchPolicyTypeMedicaid: boolean;
  targetPayerId: number | null;
  accountIds: string[];
  sourceScope: PayerAutoSelectionSourceScope;
  sourcePayerIds: number[];
  wholeSegment: boolean;
  skipOutOfState: boolean;
  prefixMatch: boolean;
  enabled: boolean;
}

const toDraft = (rule: IPayerAutoSelectionRule | null): IDraft => ({
  kind: rule?.kind ?? "PRODUCT_KEYWORD",
  keywordsText: (rule?.keywords ?? []).join("\n"),
  matchPolicyTypeMedicaid: rule?.matchPolicyTypeMedicaid ?? false,
  targetPayerId: rule?.targetPayerId ?? null,
  accountIds: rule?.accountIds ?? [],
  sourceScope: rule?.sourceScope ?? "SAME_PVERIFY_CODE",
  sourcePayerIds: rule?.sourcePayerIds ?? [],
  wholeSegment: rule?.wholeSegment ?? false,
  skipOutOfState: rule?.skipOutOfState ?? false,
  prefixMatch: rule?.prefixMatch ?? false,
  enabled: rule?.enabled ?? true,
});

/** One keyword per line; commas separate too, because a keyword never contains one. */
export const parseKeywords = (text: string): string[] => [
  ...new Set(
    text
      .split(/[\n,]/)
      .map((keyword) =>
        keyword.toUpperCase().replace(/[-_]/g, " ").replace(/\s+/g, " ").trim()
      )
      .filter(Boolean)
  ),
];

/** What is wrong with the draft, in words, or null. The API checks the same things. */
export const draftProblem = (draft: IDraft): string | null => {
  const keywords = parseKeywords(draft.keywordsText);
  if (draft.targetPayerId === null) return "Choose the payer to switch to.";
  if (draft.kind === "PRODUCT_KEYWORD" && keywords.length === 0) {
    return "Enter at least one keyword.";
  }
  if (
    draft.kind === "MEDICAID_DUAL" &&
    keywords.length === 0 &&
    !draft.matchPolicyTypeMedicaid
  ) {
    return "Enter at least one keyword, or match PolicyType = Medicaid.";
  }
  if (
    draft.sourceScope === "LISTED_PAYERS" &&
    draft.sourcePayerIds.length === 0
  ) {
    return "Choose at least one payer to switch from.";
  }
  return null;
};

/** The body the API receives: fields that do not apply to the kind are sent cleared. */
export const toSaveRequest = (
  draft: IDraft,
  priority: number
): IPayerAutoSelectionRuleSaveRequest => {
  const isKeyword = draft.kind === "PRODUCT_KEYWORD";
  return {
    kind: draft.kind,
    keywords:
      draft.kind === "OUT_OF_AREA_DEFAULT"
        ? []
        : parseKeywords(draft.keywordsText),
    matchPolicyTypeMedicaid:
      draft.kind === "MEDICAID_DUAL" && draft.matchPolicyTypeMedicaid,
    targetPayerId: draft.targetPayerId as number,
    accountIds: draft.accountIds,
    sourceScope: draft.sourceScope,
    sourcePayerIds:
      draft.sourceScope === "LISTED_PAYERS" ? draft.sourcePayerIds : [],
    wholeSegment: isKeyword && draft.wholeSegment,
    skipOutOfState: isKeyword && draft.skipOutOfState,
    prefixMatch: isKeyword && draft.prefixMatch,
    enabled: draft.enabled,
    priority,
  };
};

interface IPayerAutoSelectionRuleDialogProps {
  visible: boolean;
  /** The rule being edited, or null when adding. */
  rule: IPayerAutoSelectionRule | null;
  saving: boolean;
  /** The priority a NEW rule of this kind gets: after the ones already there. */
  nextPriorityFor: (kind: PayerAutoSelectionRuleKind) => number;
  onSave: (body: IPayerAutoSelectionRuleSaveRequest) => void;
  onHide: () => void;
}

const listOf = (data: any): any[] => (data as any)?.data || data || [];

export function PayerAutoSelectionRuleDialog({
  visible,
  rule,
  saving,
  nextPriorityFor,
  onSave,
  onHide,
}: IPayerAutoSelectionRuleDialogProps) {
  const [draft, setDraft] = useState<IDraft>(toDraft(rule));
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setDraft(toDraft(rule));
      setProblem(null);
    }
  }, [visible, rule]);

  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !visible,
  });
  const { data: accountsData } = useGetAllAccountsQuery(null, {
    skip: !visible,
  });

  const payerOptions = sortBy(
    listOf(payersData)
      .filter((payer: any) => !payer.archivedAt)
      .map((payer: any) => ({ label: payer.name, value: payer.id })),
    ["label"]
  );
  const accountOptions = sortBy(
    listOf(accountsData).map((account: any) => ({
      label: account.name,
      value: account.id,
    })),
    ["label"]
  );

  const set = (patch: Partial<IDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const save = () => {
    const found = draftProblem(draft);
    setProblem(found);
    if (found) return;
    const priority =
      rule && rule.kind === draft.kind
        ? rule.priority
        : nextPriorityFor(draft.kind);
    onSave(toSaveRequest(draft, priority));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        data-testid="payerAutoSelection.dialog.cancel"
      />
      <Button
        type="button"
        label={rule ? "Save rule" : "Add rule"}
        loading={saving}
        onClick={save}
        data-testid="payerAutoSelection.dialog.save"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={rule ? "Edit rule" : "Add a rule"}
      footer={footer}
      style={{ width: "min(680px, 95vw)" }}
      data-testid="payerAutoSelection.dialog"
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.kind">What kind of rule</Label>
          <Dropdown
            inputId="payerAutoSelection.kind"
            options={KIND_CHOICES}
            value={draft.kind}
            onChange={(event) => set({ kind: event.value })}
          />
        </div>

        {draft.kind !== "OUT_OF_AREA_DEFAULT" && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.keywords">
              Plan name contains (one per line; whole words, any comma segment)
            </Label>
            <InputTextarea
              id="payerAutoSelection.keywords"
              data-testid="payerAutoSelection.keywords"
              rows={4}
              value={draft.keywordsText}
              onChange={(event) => set({ keywordsText: event.target.value })}
            />
          </div>
        )}

        {draft.kind === "MEDICAID_DUAL" && (
          <div className="flex align-items-start gap-2">
            <Checkbox
              inputId="payerAutoSelection.matchPolicyTypeMedicaid"
              checked={draft.matchPolicyTypeMedicaid}
              onChange={(event) =>
                set({ matchPolicyTypeMedicaid: !!event.checked })
              }
            />
            <label htmlFor="payerAutoSelection.matchPolicyTypeMedicaid">
              {OPTION_TEXT.matchPolicyTypeMedicaid.label}
              <small className="block text-color-secondary">
                {OPTION_TEXT.matchPolicyTypeMedicaid.help}
              </small>
            </label>
          </div>
        )}

        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.target">Switch to</Label>
          <Dropdown
            inputId="payerAutoSelection.target"
            options={payerOptions}
            value={draft.targetPayerId}
            onChange={(event) => set({ targetPayerId: event.value ?? null })}
            placeholder="Choose a payer"
            filter
          />
          <small className="text-color-secondary">
            Only fires at a facility where this payer is set up.
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.accounts">Facilities</Label>
          <MultiSelect
            inputId="payerAutoSelection.accounts"
            options={accountOptions}
            value={draft.accountIds}
            onChange={(event) => set({ accountIds: event.value ?? [] })}
            placeholder="All facilities"
            display="chip"
            filter
          />
          <small className="text-color-secondary">
            None selected means all facilities.
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.source">Switch from</Label>
          <Dropdown
            inputId="payerAutoSelection.source"
            options={SOURCE_CHOICES}
            value={draft.sourceScope}
            onChange={(event) => set({ sourceScope: event.value })}
          />
        </div>

        {draft.sourceScope === "LISTED_PAYERS" && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.sourcePayers">
              Selected payers this rule switches from
            </Label>
            <MultiSelect
              inputId="payerAutoSelection.sourcePayers"
              options={payerOptions}
              value={draft.sourcePayerIds}
              onChange={(event) => set({ sourcePayerIds: event.value ?? [] })}
              display="chip"
              filter
            />
          </div>
        )}

        {draft.kind === "PRODUCT_KEYWORD" &&
          (["wholeSegment", "skipOutOfState", "prefixMatch"] as const).map(
            (option) => (
              <div key={option} className="flex align-items-start gap-2">
                <Checkbox
                  inputId={`payerAutoSelection.${option}`}
                  checked={draft[option]}
                  onChange={(event) => set({ [option]: !!event.checked })}
                />
                <label htmlFor={`payerAutoSelection.${option}`}>
                  {OPTION_TEXT[option].label}
                  <small className="block text-color-secondary">
                    {OPTION_TEXT[option].help}
                  </small>
                </label>
              </div>
            )
          )}

        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="payerAutoSelection.enabled"
            checked={draft.enabled}
            onChange={(event) => set({ enabled: !!event.checked })}
          />
          <label htmlFor="payerAutoSelection.enabled">Enabled</label>
        </div>

        {problem && (
          <Message
            severity="error"
            text={problem}
            data-testid="payerAutoSelection.dialog.problem"
          />
        )}
      </div>
    </Dialog>
  );
}
